import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { useTopPriceLinkage } from '../../hooks/usePriceLinkage';
import type { PriceLinkageSessionType } from '../../services/api';

interface TopPriceLinkageTableProps {
  limit?: number;
  sessionType?: PriceLinkageSessionType | string;
  windowDays?: number;
}

const getLinkageTone = (label?: string): 'direct' | 'inverse' | 'neutral' => {
  const value = (label || '').toLowerCase();
  if (value.includes('inverse')) return 'inverse';
  if (value.includes('direct')) return 'direct';
  return 'neutral';
};

const getConfidenceTone = (label?: string): 'high' | 'medium' | 'low' => {
  const value = (label || '').toLowerCase();
  if (value.includes('high')) return 'high';
  if (value.includes('medium')) return 'medium';
  return 'low';
};

const getLinkageClass = (tone: 'direct' | 'inverse' | 'neutral'): string => {
  if (tone === 'direct') return 'text-[#4ff6e6]';
  if (tone === 'inverse') return 'text-orange-400';
  return 'text-white/70';
};

const getConfidenceClass = (tone: 'high' | 'medium' | 'low'): string => {
  if (tone === 'high') return 'bg-[#4ff6e6]/15 border-[#4ff6e6]/35 text-[#4ff6e6]';
  if (tone === 'medium') return 'bg-[#D4AF37]/15 border-[#D4AF37]/35 text-[#D4AF37]';
  return 'bg-white/10 border-white/20 text-white/75';
};

const fmt = (value?: number) => (Number.isFinite(value) ? Number(value).toFixed(2) : 'N/A');

const TopPriceLinkageTable: React.FC<TopPriceLinkageTableProps> = ({
  limit = 10,
  sessionType = 'REGULAR',
  windowDays = 60,
}) => {
  const navigate = useNavigate();
  const { data, isLoading, error, isEmpty, refresh } = useTopPriceLinkage(limit, sessionType, windowDays);

  return (
    <div className="card-glass p-6 w-full max-w-none">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Top Price Linkage</h3>
          <p className="text-sm text-white/60">Sorted by qualityScore (desc) • {windowDays}d window</p>
        </div>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[...Array(5)].map((_, idx) => (
            <div key={idx} className="h-10 rounded bg-white/10" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4">
          <p className="text-sm text-brand-red/90 mb-3">Top linkage table unavailable right now.</p>
          <button type="button" onClick={() => refresh()} className="btn btn-outline text-sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </button>
        </div>
      )}

      {!isLoading && !error && isEmpty && (
        <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4 text-center">
          <p className="text-sm text-white/70">Not enough data yet.</p>
        </div>
      )}

      {!isLoading && !error && !isEmpty && (
        <div className="table-responsive">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr>
                <th className="text-left py-2 px-2 border-b border-white/10 text-white/60 text-xs uppercase tracking-wide">Ticker</th>
                <th className="text-left py-2 px-2 border-b border-white/10 text-white/60 text-xs uppercase tracking-wide">Linkage</th>
                <th className="text-left py-2 px-2 border-b border-white/10 text-white/60 text-xs uppercase tracking-wide">Confidence</th>
                <th className="text-left py-2 px-2 border-b border-white/10 text-white/60 text-xs uppercase tracking-wide">Lead Time</th>
                <th className="text-left py-2 px-2 border-b border-white/10 text-white/60 text-xs uppercase tracking-wide">Mentions Corr</th>
                <th className="text-left py-2 px-2 border-b border-white/10 text-white/60 text-xs uppercase tracking-wide">Obs</th>
                <th className="text-left py-2 px-2 border-b border-white/10 text-white/60 text-xs uppercase tracking-wide">Quality</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => {
                const linkageTone = getLinkageTone(item.readable?.linkageLabel);
                const confidenceTone = getConfidenceTone(item.readable?.confidenceLabel);
                return (
                  <tr
                    key={`${item.ticker}-${item.asOfDate}-${item.windowDays}`}
                    className="cursor-pointer transition-colors hover:bg-white/[0.03]"
                    onClick={() => navigate(`/ticker/${item.ticker}`)}
                  >
                    <td className="font-semibold text-white py-2 px-2 border-b border-white/10">${item.ticker}</td>
                    <td className="py-2 px-2 border-b border-white/10">
                      <span className={`text-xs font-semibold ${getLinkageClass(linkageTone)}`}>
                        {item.readable?.linkageLabel || 'N/A'}
                      </span>
                    </td>
                    <td className="py-2 px-2 border-b border-white/10">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-bold ${getConfidenceClass(confidenceTone)}`}>
                        {item.readable?.confidenceLabel || 'Low'}
                      </span>
                    </td>
                    <td className="text-white/80 py-2 px-2 border-b border-white/10">{item.readable?.leadTimeHours ?? 'N/A'}h</td>
                    <td className="text-white/80 py-2 px-2 border-b border-white/10">{fmt(item.raw?.corrMentions)}</td>
                    <td className="text-white/80 py-2 px-2 border-b border-white/10">{item.raw?.nObs ?? 'N/A'}</td>
                    <td className="text-white/80 py-2 px-2 border-b border-white/10">{fmt(item.raw?.qualityScore)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TopPriceLinkageTable;
